"use client";

import Link from "next/link";
import Image from "next/image";
import { useState, useEffect, useRef } from "react";
import UniversityLogo from "@public/svgs/university_logo.svg";
import { HEADER_CONSTANTS } from "@/constants/header";
import { RELATED_SITES } from "@/constants/related-site";

const CHATBOT_LINKS = [
  {
    name: "충북대 챗봇",
    href: "https://rise.chungbuk.ac.kr/cbnu_chatbot",
    src: "/chatbot1.png",
  },
  {
    name: "교원대 챗봇",
    href: "https://rise.chungbuk.ac.kr/knue_chatbot",
    src: "/chatbot2.png",
  },
  {
    name: "교통대 챗봇",
    href: "https://rise.chungbuk.ac.kr/knut_chatbot",
    src: "/chatbot3.png",
  },
];

/**
 * 웹사이트 푸터 컴포넌트
 *
 * 웹사이트 하단에 표시되는 푸터로, 로고, 관련사이트 드롭다운, 연락처 정보, 저작권 정보를 포함합니다.
 * 반응형 디자인을 지원하며, 관련사이트 드롭다운은 외부 클릭 시 자동으로 닫힙니다.
 *
 * 주요 기능:
 * - 충북대학교 RISE사업단 로고 및 홈 링크
 * - 관련사이트 드롭다운 메뉴
 * - 주소 및 연락처 정보 표시
 * - 저작권 정보
 * - 외부 클릭 시 드롭다운 자동 닫기
 * - 반응형 디자인 (모바일/데스크톱)
 */
export function Footer() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  /**
   * 드롭다운 토글 함수
   *
   * 관련사이트 드롭다운의 열기/닫기 상태를 전환합니다.
   */
  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  /**
   * 외부 클릭 시 드롭다운 닫기
   *
   * 드롭다운이 열려있을 때 외부를 클릭하면 자동으로 닫힙니다.
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  return (
    <footer className="w-full bg-footer text-footer-foreground py-8 sm:py-10">
      <div className="max-w-[1540px] mx-auto px-5">
        {/* 상단 영역: 로고 및 관련사이트 */}
        <div className="pb-8 border-b border-white/10">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-3">
            {/* 로고 및 브랜드명 */}
            <div className="flex-shrink-0 scale-75 sm:scale-100 origin-left">
              <Link
                href="/"
                className="flex items-center brightness-0 invert"
                aria-label="홈으로 이동"
              >
                <UniversityLogo
                  className={`h-[70px] w-[${HEADER_CONSTANTS.STYLES.LOGO_WIDTH}px]`}
                  aria-label="충북대학교 RISE사업단 로고"
                />
                <div className="w-0.5 h-6 bg-border ml-2 mr-4 mb-1.5 opacity-50" />
                <p className="text-[22px] font-extrabold text-foreground mb-1.5 text-nowrap">
                  RISE사업단
                </p>
              </Link>
            </div>

            <div className="flex flex-col items-center gap-1 md:ml-auto">
              <span className="w-full text-center text-base font-medium text-footer-foreground">
                대학별 챗봇
              </span>
              <div className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/5 px-4 py-3">
                {CHATBOT_LINKS.map((chatbot) => (
                  <a
                    key={chatbot.name}
                    href={chatbot.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center rounded-md p-2 transition hover:bg-white/10"
                    aria-label={`${chatbot.name} 바로가기`}
                  >
                    <Image
                      src={chatbot.src}
                      alt={chatbot.name}
                      width={567}
                      height={567}
                      className="h-14 w-14 sm:h-16 sm:w-16 object-cover"
                    />
                  </a>
                ))}
              </div>
            </div>

            {/* 관련사이트 드롭다운 */}
            <div className="md:text-right md:ml-2">
              <div
                className="relative inline-block text-left"
                ref={dropdownRef}
              >
                <button
                  onClick={toggleDropdown}
                  className="inline-flex items-center px-4 py-2 border border-white/10 rounded-lg text-footer-foreground hover:bg-white/5"
                >
                  <span className="text-sm font-medium">관련사이트</span>
                  {/* 드롭다운 화살표 */}
                  <svg
                    className={`ml-20 h-3 w-3 ${
                      isDropdownOpen ? "rotate-180" : ""
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>

                {/* 관련사이트 드롭다운 메뉴 */}
                {isDropdownOpen && (
                  <div className="absolute left-0 md:left-auto md:right-0 mt-2 w-56 bg-footer border border-white/10 rounded-lg shadow-lg z-50">
                    <div className="py-2">
                      {RELATED_SITES.map((site, index) => (
                        <a
                          key={index}
                          href={site.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block px-4 py-2 text-sm text-footer-foreground hover:bg-white/5 hover:text-white"
                        >
                          {site.name}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* 중간 영역: 주소 및 연락처 */}
        <div className="py-6 border-b border-white/10">
          <div className="flex flex-col gap-1">
            <div className="space-y-2">
              {/* 주소 정보 */}
              <div className="text-footer-foreground text-sm">
                (28644) 충북 청주시 서원구 충대로 1, 충북대학교 E9동 906호
              </div>
            </div>

            {/* 연락처 정보 */}
            <div className="flex mt-2 sm:mt-0 gap-0 sm:gap-12 flex-col sm:flex-row">
              <div className="text-footer-foreground text-sm">
                <b className="mr-1">TEL</b> 043) 249-1800
              </div>
              <div className="text-footer-foreground text-sm">
                <b className="mr-1">FAX</b> 043) 271-0809
              </div>
            </div>
          </div>
        </div>

        {/* 하단 영역: 저작권 정보 */}
        <div className="pt-6">
          <div className="text-footer-foreground/50 text-sm text-center md:text-left">
            ©2025. 충북대학교 RISE사업단. All Rights Reserved.
          </div>
        </div>
      </div>
    </footer>
  );
}
